import { ref, readonly, onUnmounted, watch  } from 'vue'
import { useUserStore } from '@/store/user'
import { CONFIG_INACTIVITY_TIMEOUT_IN_SECOND, CONFIG_WARNING_DURATION_IN_SECOND } from '@/config'
import { usePathController } from '../composables/usePathController'

const INACTIVITY_TIMEOUT = CONFIG_INACTIVITY_TIMEOUT_IN_SECOND || 360
const WARNING_DURATION   = CONFIG_WARNING_DURATION_IN_SECOND  || 60

export function useInactivityTracker() {
  const userStore = useUserStore()
  const { logoutCoordinator } = usePathController()
  const showWarning = ref(false)
  const remainingSeconds = ref(WARNING_DURATION)

  let lastActivityAt = Date.now()
  let ticker = null
  let rafScheduled = false
  let sessionVersion = 0
  const registerActivity = () => {
    if (!userStore.userId) return
    if (showWarning.value) return 

    if (rafScheduled) return
    rafScheduled = true

    requestAnimationFrame(() => {
      rafScheduled = false
      lastActivityAt = Date.now()
    })
  }

  const tick = () => {
    if (!userStore.userId) return
    const currentVersion = sessionVersion
    const now = Date.now()
    const idleSeconds = Math.floor((now - lastActivityAt) / 1000)
    const WARNING_START = INACTIVITY_TIMEOUT - WARNING_DURATION
    
    if (idleSeconds >= WARNING_START && idleSeconds < INACTIVITY_TIMEOUT) {
      showWarning.value = true
      remainingSeconds.value = INACTIVITY_TIMEOUT - idleSeconds
      return
    }

    if (idleSeconds >= INACTIVITY_TIMEOUT) {
      if (currentVersion !== sessionVersion) return
      forceLogout()
    }
  }

  const startTicker = () => {
    if (ticker) return
    ticker = window.setInterval(tick, 1000)
  }

  const stopTicker = () => {
    if (ticker) {
      clearInterval(ticker)
      ticker = null
    }
  }

  const handleStayLoggedIn = async () => {
    sessionVersion++

    const now = Date.now()
    lastActivityAt = now

    showWarning.value = false
  }

  const forceLogout = async () => {
    stopTicker()
    showWarning.value = false

    await logoutCoordinator()
  }

  startTicker()

  onUnmounted(() => {
    stopTicker()
  })

  watch(
  () => userStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      lastActivityAt = Date.now()
      showWarning.value = false
    }
  },
  { immediate: true }
)

  return {
    showWarning: readonly(showWarning),
    remainingSeconds: readonly(remainingSeconds),
    handleStayLoggedIn,
    forceLogout,
    registerActivity
  }
}
